/* eslint-disable id-blacklist */
import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import {
  AmenityService,
  CreateRideSubscriptionRequest,
  CustomerServiceNoteService,
  FavoriteLocation,
  ItinerarySanitizer,
  ItineraryService,
  LocationErrorConstants,
  OrganizationRiderService,
  OrganizationService,
  Payment,
  PaymentAccountErrorConstants,
  PaymentAccountService,
  PaymentMethod,
  PaymentUtil,
  PersonSuspension,
  PersonSuspensionDetail,
  RideErrorConstants,
  RideRequestService,
  RiderManagementService,
  RideSubscriptionService,
  SuspensionReasonService,
  TransitProgramService,
  Trip,
  VehicleService,
  VehicleType
} from '@uzurv/clara';
import {
  ApplicationException,
  DateFormatConstants,
  DateUtil,
  HttpException,
  isNullOrUndefined,
  ObjectConstants,
  PaymentMethodConstants,
  PaymentStatusConstants,
  PermissionsConstants,
  ServiceLevelConstants,
  ServiceLevelType,
  TimeZoneUtil,
  VehicleTypeConstants
} from '@uzurv/core';
import moment from 'moment-timezone';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { AddOrganizationRiderDialogComponent } from '../../components/dialogs/add-organziation-rider-dialog/add-organization-rider-dialog.component';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { EditOrganizationRiderDialogComponent } from '../../components/dialogs/edit-organization-rider-dialog/edit-organization-rider-dialog.component';
import { EditRiderDialogComponent } from '../../components/dialogs/edit-rider-dialog';
import { NewPaymentDialogComponent } from '../../components/dialogs/new-payment-dialog/new-payment-dialog.component';
import {
  PersonSuspensionDialog,
  PersonSuspensionDialogResults,
  PersonSuspensionTypeEnum
} from '../../components/dialogs/person-suspension-dialog/person-suspension-dialog';
import { RefundPaymentDialogComponent } from '../../components/dialogs/refund-payment-dialog/refund-payment-dialog.component';
import { RiderHasBalanceDialogComponent } from '../../components/dialogs/rider-has-balance-dialog/rider-has-balance-dialog.component';
import { ItineraryConstants } from '../../constants';
import { NewItineraryValidationService } from '../../pages/new-itinerary/new-itinerary-validation.service';
import { RiderDetailTabs } from '../../pages/organization-rider-detail/organization-rider-detail.page';
import * as OrganizationRiderActions from './organization-rider.actions';
import { GetRiderTripsFail, GetRiderTripsSuccess } from './organization-rider.actions';
import { OrganizationRiderState } from './organization-rider.reducer';
import { getLatestChargeOrder, getRider, organizationRiderState } from './organization-rider.selectors';
import { AuthDataService } from '@uzurv/http';
import * as TabActions from '../../store/tab/tab.actions';

@Injectable()
export class OrganizationRiderEffects {
  openOrganizationRiderDetail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenOrganizationRiderDetail),
        map(action => {
          this.router.navigate(
            [`/riders/details/${action.organizationRider.organizationRiderId}/organization/${action.organizationRider.organizationId}`],
            {
              queryParams: {
                subTab: action.subTab ?? RiderDetailTabs.BASIC_INFO
              }
            }
          );
        })
      );
    },
    { dispatch: false }
  );

  confirmCancelAddNewRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ConfirmCancelAddNewRider),
      switchMap(action =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: 'Are you sure you want to cancel? All unsaved data will be lost.',
              isDestructive: true
            }
          })
          .afterClosed()
          .pipe(
            map(confirmed =>
              confirmed
                ? OrganizationRiderActions.CancelAddNewRider({ payload: { forNewItinerary: action.payload.forNewItinerary } })
                : OrganizationRiderActions.AbandonSubmission()
            )
          )
      )
    );
  });

  createOrganizationRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.CreateOrganizationRider),
      switchMap(action =>
        this.organizationRiderService.createOrganizationRider(action.payload.organizationRider).pipe(
          map(organizationRiderDetail => {
            // Add the service levels
            action.payload.organizationRiderServiceLevels.forEach(orsl => {
              orsl.organizationRiderId = organizationRiderDetail.organizationRiderId;
              if (orsl.enabled) {
                this.store$.dispatch(OrganizationRiderActions.EnableOrganizationRiderServiceLevel({ organizationRiderServiceLevel: orsl }));
              } else {
                this.store$.dispatch(
                  OrganizationRiderActions.DisableOrganizationRiderServiceLevel({ organizationRiderServiceLevel: orsl })
                );
              }
            });

            return OrganizationRiderActions.CreateOrganizationRiderSuccess({
              payload: {
                organizationRiderId: organizationRiderDetail.organizationRiderId,
                organizationId: organizationRiderDetail.organizationId,
                organizationRiderDetail: organizationRiderDetail,
                organization: action.payload.organization,
                forNewItinerary: action.payload.forNewItinerary,
                forAddingOrganizationRider: isNullOrUndefined(action.payload.forAddingOrganizationRider)
                  ? false
                  : action.payload.forAddingOrganizationRider
              }
            });
          }),
          catchError(error => {
            error = this.checkDuplicateCustomerIdError(error);
            return of(OrganizationRiderActions.CreateOrganizationRiderFail({ error }));
          })
        )
      )
    );
  });

  createOrganizationRiderSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.CreateOrganizationRiderSuccess),
        map(action =>
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Success',
              message: `Added ${action.payload.organizationRiderDetail.firstName} ${action.payload.organizationRiderDetail.lastName} to ${action.payload.organization.name}`
            }
          })
        )
      );
    },
    { dispatch: false }
  );

  createRiderWithProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevels),
      switchMap(action =>
        this.riderManagementService.createRiderWithProfilesAndServiceLevels(action.riderCreationRequest).pipe(
          map(rider => OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevelsSuccess({ rider })),
          catchError(error => {
            error = this.checkDuplicateCustomerIdError(error);
            return of(OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevelsFail({ error }));
          })
        )
      )
    )
  );

  createRiderWithProfilesSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevelsSuccess),
        map(action =>
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                title: 'Success',
                message: `Added ${action.rider.firstName} ${action.rider.lastName}`
              }
            })
            .afterClosed()
            .pipe(
              map(() => {
                this.store$.dispatch(OrganizationRiderActions.SearchRiderNewItinerary({ searchString: `${action.rider.phoneNumber}` }));
              })
            )
            .subscribe()
        )
      );
    },
    { dispatch: false }
  );

  getAutocompleteResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SearchRider, OrganizationRiderActions.ShowMoreResults),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.organizationRiderService.searchRiders(state.searchString, 0, state.riderPageSize).pipe(
          map(organizationRiderListItems => OrganizationRiderActions.RiderSearchSuccess({ organizationRiderListItems })),
          catchError(error => of(OrganizationRiderActions.RiderSearchFail({ error })))
        )
      )
    );
  });

  getAutocompleteResultsNewItinerary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SearchRiderNewItinerary),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.organizationRiderService.searchRiders(state.searchStringNewItinerary, 0, state.riderPageSize).pipe(
          map(organizationRiderListItems => OrganizationRiderActions.SearchRiderNewItinerarySuccess({ organizationRiderListItems })),
          catchError(error => of(OrganizationRiderActions.SearchRiderNewItineraryFail({ error })))
        )
      )
    );
  });

  start$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.Start),
        map(() => {
          this.store$.dispatch(OrganizationRiderActions.GetVehicleTypes());
          this.store$.dispatch(OrganizationRiderActions.GetAmenities());
          this.store$.dispatch(OrganizationRiderActions.GetRideRequestTypes());
        })
      );
    },
    { dispatch: false }
  );

  getRiderProfileDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      switchMap(action => {
        return this.organizationRiderService.getRiderProfileByOrganizationRiderId(action.payload.organizationRiderId).pipe(
          map(riderProfile => {
            return OrganizationRiderActions.GetRiderProfileSuccess({ riderProfile });
          }),
          catchError(error => of(OrganizationRiderActions.GetRiderProfileFail({ error })))
        );
      })
    );
  });

  updateRiderMobilityProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.UpdateRiderMobilityProfile),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([action, rider]) => {
        if (
          !this.authDataService.hasPermission({
            any: [PermissionsConstants.MOBILITY_PROFILE_WRITE],
            all: []
          })
        ) {
          this.snackBar.open('You do not have permission to update the rider’s mobility profile.', null, {
            duration: 5000
          });
          return of(OrganizationRiderActions.UpdateRiderMobilityProfileFail({ error: new HttpException(403, 'Permission Denied') }));
        }

        return this.riderManagementService
          .updateRiderMobilityProfileByOrganizationRiderId(rider.riderId, action.mobilityProfileDetail)
          .pipe(
            map(() => {
              this.snackBar.open('Rider’s mobility profile has been saved!', null, {
                duration: 5000
              });
              return OrganizationRiderActions.UpdateRiderMobilityProfileSuccess({
                mobilityProfileDetail: action.mobilityProfileDetail
              });
            }),
            catchError(error => {
              return of(OrganizationRiderActions.UpdateRiderMobilityProfileFail({ error }));
            })
          );
      })
    );
  });

  verifyRiderMobilityProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.VerifyRiderMobilityProfile),
      withLatestFrom(this.store$.select(getRider)),

      switchMap(([action, rider]) => {
        if (
          !this.authDataService.hasPermission({
            any: [PermissionsConstants.MOBILITY_PROFILE_WRITE],
            all: []
          })
        ) {
          this.snackBar.open('You do not have permission to update the rider’s mobility profile.', null, {
            duration: 5000
          });
          return of(OrganizationRiderActions.UpdateRiderMobilityProfileFail({ error: new HttpException(403, 'Permission Denied') }));
        }

        return this.riderManagementService
          .verifyRiderMobilityProfileByOrganizationRiderId(rider.riderId, action.mobilityProfileDetail)
          .pipe(
            map(mobilityProfileDetail => {
              this.snackBar.open('Rider’s mobility profile has been verified!', null, {
                duration: 5000
              });
              return OrganizationRiderActions.VerifyRiderMobilityProfileSuccess({
                mobilityProfileDetail
              });
            }),
            catchError(error => {
              return of(OrganizationRiderActions.VerifyRiderMobilityProfileFail({ error }));
            })
          );
      })
    );
  });

  setDefaultVehicleType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRiderProfileSuccess, OrganizationRiderActions.GetVehicleTypesSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(
        ([action, state]) => state.vehicleTypes?.length > 0 && !isNullOrUndefined(state.riderMobilityProfile?.recommendedServiceLevel)
      ),
      map(([action, state]) => {
        const basicVehicleType: VehicleType = state.vehicleTypes.find(
          vehicleType => vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC
        );

        const wavVehicleType: VehicleType = state.vehicleTypes.find(
          vehicleType => vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV
        );

        return OrganizationRiderActions.SelectVehicleType({
          vehicleType: state.riderMobilityProfile.recommendedServiceLevel.isNonAmbulatory() ? wavVehicleType : basicVehicleType
        });
      })
    );
  });

  getVehicleTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetVehicleTypes),
      switchMap(() =>
        this.vehicleService.getVehicleTypes().pipe(
          map(vehicleTypes => OrganizationRiderActions.GetVehicleTypesSuccess({ vehicleTypes })),
          catchError(error => of(OrganizationRiderActions.GetVehicleTypesFail({ error })))
        )
      )
    );
  });

  getAmenities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetAmenities),
      switchMap(() =>
        this.amenityService.getAmenities().pipe(
          map(amenities => OrganizationRiderActions.GetAmenitiesSuccess({ amenities })),
          catchError(error => of(OrganizationRiderActions.GetAmenitiesFail({ error })))
        )
      )
    );
  });

  getRideRequestTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRideRequestTypes),
      switchMap(() =>
        this.rideRequestService.listRideRequestTypes().pipe(
          map(rideRequestTypes => OrganizationRiderActions.GetRideRequestTypesSuccess({ rideRequestTypes })),
          catchError(error => of(OrganizationRiderActions.GetRideRequestTypesFail({ error })))
        )
      )
    );
  });

  getRideMetadataFields$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRideMetadataFields),
      switchMap(action =>
        this.organizationService
          .listRideMetadataFieldsForTransitProgram(action.payload.organizationId, action.payload.transitProgramId)
          .pipe(
            map(rideMetadataFields => OrganizationRiderActions.GetRideMetadataFieldsSuccess({ rideMetadataFields })),
            catchError(error => of(OrganizationRiderActions.GetRideMetadataFieldsFail({ error })))
          )
      )
    );
  });

  riderSelected$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SelectRider, OrganizationRiderActions.CreateOrganizationRiderSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([action, state]) => {
        if (action.type === OrganizationRiderActions.CreateOrganizationRiderSuccess.type) {
          return state.newItineraryStarted;
        }

        return true;
      }),
      map(([action, state]) =>
        OrganizationRiderActions.GetOrganizationRiderDetail({
          payload: {
            organizationRiderId: action.payload.organizationRiderId,
            organizationId: action.payload.organizationId
          }
        })
      )
    );
  });

  getOrganizationRiderDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.GetOrganizationRiderDetail,
        OrganizationRiderActions.UpdateOrganizationRiderSuccess,
        OrganizationRiderActions.RemoveFavoriteDriverSuccess,
        OrganizationRiderActions.SubmitPaymentForOutstandingChargesSuccess,
        OrganizationRiderActions.CreatePaymentSuccess,
        OrganizationRiderActions.VoidPaymentSuccess,
        OrganizationRiderActions.ResubmitPaymentSuccess,
        OrganizationRiderActions.RefundPaymentSuccess
      ),
      switchMap(action =>
        this.organizationRiderService.findOrganizationRider(action.payload.organizationId, action.payload.organizationRiderId).pipe(
          map(organizationRiderDetail => OrganizationRiderActions.GetOrganizationRiderDetailSuccess({ payload: organizationRiderDetail })),
          catchError(error => of(OrganizationRiderActions.GetOrganizationRiderDetailFail({ error })))
        )
      )
    );
  });

  updateOrganizationRiderEligibility$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.UpdateOrganizationRiderEligibility),
      switchMap(action => {
        return this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: 'Save changes to this rider?',
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                action.organizationRiderDetail.eligible = action.isRiderEligible;
                return this.organizationRiderService.patchOrganizationRider(action.organizationRiderDetail).pipe(
                  map(organizationRiderDetail => {
                    return OrganizationRiderActions.UpdateOrganizationRiderEligibilitySuccess({
                      payload: {
                        organizationRiderDetail: organizationRiderDetail,
                        isRiderEligible: action.isRiderEligible
                      }
                    });
                  }),
                  catchError(error => of(OrganizationRiderActions.UpdateOrganizationRiderEligibilityFail({ error })))
                );
              }
              return of(
                OrganizationRiderActions.UpdateOrganizationRiderEligibilitySuccess({
                  payload: {
                    organizationRiderDetail: action.organizationRiderDetail,
                    isRiderEligible: action.organizationRiderDetail.eligible
                  }
                })
              );
            })
          );
      })
    );
  });

  getOrganizations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      switchMap(() =>
        this.organizationService.listAll().pipe(
          map(organizations => OrganizationRiderActions.GetOrganizationsSuccess({ organizations })),
          catchError(error => of(OrganizationRiderActions.GetOrganizationsFail({ error })))
        )
      )
    );
  });

  refreshOrganizationRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.Resume),
      withLatestFrom(this.store$.select(getRider)),
      filter(([_, rider]) => !isNullOrUndefined(rider?.organizationId) && !isNullOrUndefined(rider?.organizationRiderId)),
      switchMap(([_, rider]) =>
        this.organizationRiderService.findOrganizationRider(rider.organizationId, rider.organizationRiderId).pipe(
          map(organizationRiderDetail => OrganizationRiderActions.GetOrganizationRiderDetailSuccess({ payload: organizationRiderDetail })),
          catchError(error => of(OrganizationRiderActions.GetOrganizationRiderDetailFail({ error })))
        )
      )
    );
  });

  shouldGetRideLimits$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess, OrganizationRiderActions.SelectDate),
        map(_ => this.store$.dispatch(OrganizationRiderActions.GetRideLimits()))
      );
    },
    { dispatch: false }
  );

  doesRiderHaveRemainingRides$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRideLimits),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => (state.newItineraryStarted && state.itineraryDate != null) || !state.newItineraryStarted),
      switchMap(([_, state]) =>
        this.organizationRiderService
          .getRideLimits(
            state.rider.organizationRiderId,
            state.newItineraryStarted && state.itineraryDate ? state.itineraryDate : new Date()
          )
          .pipe(
            map(rideLimitDetail => {
              if (
                state.newItineraryStarted &&
                (rideLimitDetail.riderRidesRemaining === 0 ||
                  rideLimitDetail.organizationAmbulatoryRidesRemaining === 0 ||
                  rideLimitDetail.organizationWavRidesRemaining === 0)
              ) {
                let message = 'The rider or the organization has hit their limit of available rides:<br><br>';
                if (rideLimitDetail.riderRidesRemaining === 0) {
                  message += `Rider Rides Remaining: <strong>${rideLimitDetail.riderRidesRemaining}</strong> (for the ${
                    rideLimitDetail.riderRideLimitPeriod
                  } of ${moment(rideLimitDetail.riderRideLimitPeriodStartDate).format(
                    DateFormatConstants.SHORT_MONTH_ORDINAL_DAY
                  )} - ${moment(rideLimitDetail.riderRideLimitPeriodEndDate).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY)})<br>`;
                }
                if (rideLimitDetail.organizationAmbulatoryRidesRemaining === 0) {
                  message += `Ambulatory Organization Rides Remaining: <strong>${
                    rideLimitDetail.organizationAmbulatoryRidesRemaining
                  }</strong> (for the ${rideLimitDetail.organizationRideLimitPeriod} of ${moment(
                    rideLimitDetail.organizationRideLimitPeriodStartDate
                  ).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY)} - ${moment(
                    rideLimitDetail.organizationRideLimitPeriodEndDate
                  ).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY)})<br>`;
                }
                if (rideLimitDetail.organizationWavRidesRemaining === 0) {
                  message += `Organization WAV Rides Remaining: <strong>${
                    rideLimitDetail.organizationWavRidesRemaining
                  }</strong> (for the ${rideLimitDetail.organizationRideLimitPeriod} of ${moment(
                    rideLimitDetail.organizationRideLimitPeriodStartDate
                  ).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY)} - ${moment(
                    rideLimitDetail.organizationRideLimitPeriodEndDate
                  ).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY)})`;
                }
                this.dialog.open(AlertDialogComponent, {
                  data: {
                    title: 'No rides remaining',
                    message: message
                  }
                });
              }
              return OrganizationRiderActions.GetRideLimitsSuccess({ rideLimitDetail });
            }),
            catchError(error => of(OrganizationRiderActions.GetRideLimitsFail({ error })))
          )
      )
    );
  });

  getOrganizationServiceLevels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.organizationService.listOrganizationServiceLevels(state.rider.organizationId).pipe(
          map(serviceLevels =>
            OrganizationRiderActions.GetOrganizationServiceLevelsSuccess({
              organizationServiceLevels: serviceLevels.sort((a, b) => a.serviceLevel.name.localeCompare(b.serviceLevel.name))
            })
          ),
          catchError(error => of(OrganizationRiderActions.GetOrganizationServiceLevelsFail({ error })))
        )
      )
    );
  });

  getRiderPaymentAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.GetOrganizationRiderDetailSuccess,
        OrganizationRiderActions.CreatePaymentAccountSuccess,
        OrganizationRiderActions.DeletePaymentAccountSuccess,
        OrganizationRiderActions.EditPaymentAccountSuccess,
        OrganizationRiderActions.DeletePaymentAccountSuccess,
        OrganizationRiderActions.Resume
      ),
      withLatestFrom(this.store$.select(getRider)),
      filter(([_, rider]) => !isNullOrUndefined(rider?.organizationRiderId)),
      switchMap(([_, rider]) =>
        this.organizationRiderService.getPaymentAccountsByOrganizationRiderId(rider.organizationRiderId).pipe(
          map(paymentAccounts => OrganizationRiderActions.GetRiderPaymentAccountsSuccess({ paymentAccounts })),
          catchError(error => of(OrganizationRiderActions.GetRiderPaymentAccountsFail({ error })))
        )
      )
    );
  });

  getRiderOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([_, rider]) =>
        this.organizationService.findById(rider.organizationId).pipe(
          map(organization => OrganizationRiderActions.GetOrganizationSuccess({ organization })),
          catchError(error => of(OrganizationRiderActions.GetOrganizationFail({ error })))
        )
      )
    );
  });

  doesRiderHaveBalance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(
        ([_, state]) =>
          this.authDataService.hasPermission({ any: ['payment_processing'], all: [] }) &&
          state.organization.riderPaymentRequired &&
          state.organization.riderPaymentAccountEnabled &&
          state.rider.balance > 0 &&
          state.newItineraryStarted
      ),
      switchMap(([_, state]) =>
        this.dialog
          .open(RiderHasBalanceDialogComponent, {
            data: {
              organizationRider: state.rider
            }
          })
          .afterClosed()
          .pipe(map(() => OrganizationRiderActions.Close({})))
      )
    );
  });

  getRiderFavoriteLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRiderFavoriteLocations, OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) =>
        this.organizationRiderService
          .getFavoriteLocationsByRiderId(
            action.payload.organizationRiderId ? action.payload.organizationRiderId : state.rider.organizationRiderId
          )
          .pipe(
            map(favoriteLocations => OrganizationRiderActions.GetRiderFavoriteLocationsSuccess({ favoriteLocations })),
            catchError(error => of(OrganizationRiderActions.GetRiderFavoriteLocationsFail({ error })))
          )
      )
    );
  });

  getOrganizationRiderServiceLevels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.GetOrganizationRiderDetailSuccess,
        OrganizationRiderActions.EnableOrganizationRiderServiceLevelSuccess,
        OrganizationRiderActions.DisableOrganizationRiderServiceLevelSuccess
      ),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => !isNullOrUndefined(state.rider)),
      switchMap(([_, state]) =>
        this.organizationRiderService.listOrganizationRiderServiceLevels(state.rider.organizationRiderId).pipe(
          map(organizationRiderServiceLevels =>
            OrganizationRiderActions.GetOrganizationRiderServiceLevelsSuccess({ organizationRiderServiceLevels })
          ),
          catchError(error => of(OrganizationRiderActions.GetOrganizationRiderServiceLevelsFail({ error })))
        )
      )
    );
  });

  getOrganizationRiderAddress = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => !isNullOrUndefined(state.rider)),
      switchMap(([_, state]) =>
        this.organizationRiderService.getOrganizationRiderAddress(state.rider.riderId).pipe(
          map(address => {
            return OrganizationRiderActions.GetOrganizationRiderAddressSuccess({ address });
          }),
          catchError(error => of(OrganizationRiderActions.GetOrganizationRiderAddressFail({ error })))
        )
      )
    );
  });

  enableOrganizationRiderServiceLevel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.EnableOrganizationRiderServiceLevel),
      withLatestFrom(this.store$.select(organizationRiderState)),
      mergeMap(([action, state]) =>
        this.organizationRiderService
          .addOrganizationRiderServiceLevel(action.organizationRiderServiceLevel.organizationRiderId, action.organizationRiderServiceLevel)
          .pipe(
            map(organizationRiderServiceLevel =>
              OrganizationRiderActions.EnableOrganizationRiderServiceLevelSuccess({ organizationRiderServiceLevel })
            ),
            catchError(error => of(OrganizationRiderActions.EnableOrganizationRiderServiceLevelFail({ error })))
          )
      )
    );
  });

  disableOrganizationRiderServiceLevel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.DisableOrganizationRiderServiceLevel),
      mergeMap(action =>
        this.organizationRiderService
          .disableOrganizationRiderServiceLeve(
            action.organizationRiderServiceLevel.organizationRiderId,
            action.organizationRiderServiceLevel.serviceLevel.id
          )
          .pipe(
            map(organizationRiderServiceLevel => OrganizationRiderActions.DisableOrganizationRiderServiceLevelSuccess(null)),
            catchError(error => of(OrganizationRiderActions.DisableOrganizationRiderServiceLevelFail({ error })))
          )
      )
    );
  });

  replaceFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ReplaceFavoriteLocation),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([action, rider]) =>
        this.organizationRiderService.deleteFavoriteLocation(rider.organizationRiderId, action.payload.toDelete.location.id).pipe(
          switchMap(success => {
            this.store$.dispatch(OrganizationRiderActions.DeleteFavoriteLocationSuccess({ favoriteLocation: action.payload.toDelete }));

            return this.organizationRiderService
              .addFavoriteLocationForRiderWithLabel(
                rider.organizationRiderId,
                action.payload.toCreate.location,
                action.payload.toCreate.label
              )
              .pipe(
                map(favoriteLocation => OrganizationRiderActions.AddFavoriteLocationSuccess({ favoriteLocation })),
                catchError(error =>
                  of(
                    OrganizationRiderActions.AddFavoriteLocationFail({
                      payload: {
                        location: action.payload.toCreate.location,
                        label: action.payload.toCreate.label,
                        error: error
                      }
                    })
                  )
                )
              );
          }),
          catchError(error => of(OrganizationRiderActions.DeleteFavoriteLocationFail({ error })))
        )
      )
    );
  });

  addFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.AddFavoriteLocation),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([action, rider]) =>
        this.organizationRiderService
          .addFavoriteLocationForRiderWithLabel(rider.organizationRiderId, action.payload.location, action.payload.label)
          .pipe(
            map(favoriteLocation => OrganizationRiderActions.AddFavoriteLocationSuccess({ favoriteLocation })),
            catchError(error =>
              of(
                OrganizationRiderActions.AddFavoriteLocationFail({
                  payload: { location: action.payload.location, label: action.payload.label, error: error }
                })
              )
            )
          )
      )
    );
  });

  deleteFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.DeleteFavoriteLocation),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([action, rider]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are you sure?',
              message: `This will delete the favorite location with label ${action.favoriteLocation.label}`
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService
                  .deleteFavoriteLocation(rider.organizationRiderId, action.favoriteLocation.location.id)
                  .pipe(
                    map(success => OrganizationRiderActions.DeleteFavoriteLocationSuccess({ favoriteLocation: action.favoriteLocation })),
                    catchError(error => of(OrganizationRiderActions.DeleteFavoriteLocationFail({ error })))
                  );
              }

              return null;
            })
          )
      )
    );
  });

  confirmSubmission$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ItineraryValidated),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) => {
        if (state.isRideSubscription) {
          let days = '';

          if (state.selectedDaysOfTheWeek.length === 1) {
            days = ItineraryConstants.DaysOfTheWeek[state.selectedDaysOfTheWeek[0].toUpperCase()].abbreviation;
          } else if (state.selectedDaysOfTheWeek.length === 2) {
            days =
              ItineraryConstants.DaysOfTheWeek[state.selectedDaysOfTheWeek[0].toUpperCase()].abbreviation +
              ' and ' +
              ItineraryConstants.DaysOfTheWeek[state.selectedDaysOfTheWeek[1].toUpperCase()].abbreviation;
          } else if (state.selectedDaysOfTheWeek.length > 2) {
            for (let d = 0; d < state.selectedDaysOfTheWeek.length; d++) {
              if (d !== state.selectedDaysOfTheWeek.length - 1) {
                days += ItineraryConstants.DaysOfTheWeek[state.selectedDaysOfTheWeek[d].toUpperCase()].abbreviation + ', ';
              } else {
                days += 'and ';
                days += ItineraryConstants.DaysOfTheWeek[state.selectedDaysOfTheWeek[d].toUpperCase()].abbreviation;
              }
            }
          }

          return this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                title: 'Are you sure?',
                message: `A new itinerary will be created for ${state.rider.firstName} ${state.rider.lastName} starting on ${moment(
                  state.subscriptionStartDate
                ).format('MM/DD/YYYY')}
                <br><br>
                A ride subscription will repeat this itinerary every ${days} until ${moment(state.subscriptionEndDate).format(
                  'MM/DD/YYYY'
                )}`
              }
            })
            .afterClosed()
            .pipe(
              map(confirmed =>
                confirmed ? OrganizationRiderActions.ConfirmRideSubscriptionSubmission() : OrganizationRiderActions.AbandonSubmission()
              )
            );
        } else {
          return this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                title: 'Are you sure?',
                message: `A new itinerary will be created for ${state.rider.firstName} ${state.rider.lastName} on ${moment(
                  state.itineraryDate
                ).format('MM/DD/YYYY')}`
              }
            })
            .afterClosed()
            .pipe(
              map(confirmed => (confirmed ? OrganizationRiderActions.ConfirmSubmission() : OrganizationRiderActions.AbandonSubmission()))
            );
        }
      })
    );
  });

  validateItinerary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SubmitItinerary),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.newItineraryValidationService.validate(state).pipe(
          switchMap(errors => {
            if (errors.length === 0) {
              // Check selected service level against recommended service level
              // TODO: This is not the best place for this logic, but it's the least impactful in terms of refactoring the whole itinerary creation flow
              const recommendedServiceLevel = state.riderMobilityProfile?.recommendedServiceLevel;

              if (
                (recommendedServiceLevel?.isAmbulatory() && state.selectedServiceLevelType === ServiceLevelType.NON_AMBULATORY) ||
                (recommendedServiceLevel?.isNonAmbulatory() && state.selectedServiceLevelType === ServiceLevelType.AMBULATORY)
              ) {
                return this.dialog
                  .open(ConfirmationDialogComponent, {
                    data: {
                      title: 'Are you sure?',
                      message: `The ${state.selectedVehicleType.name} Service Level is not recommended based on the rider's mobility profile. <br />Please confirm with the rider before proceeding with this selection.`
                    }
                  })
                  .afterClosed()
                  .pipe(
                    map(confirmed =>
                      confirmed ? OrganizationRiderActions.ItineraryValidated() : OrganizationRiderActions.AbandonSubmission()
                    )
                  );
              } else {
                return of(OrganizationRiderActions.ItineraryValidated());
              }
            } else {
              return of(OrganizationRiderActions.ItineraryInvalid({ payload: errors }));
            }
          })
        )
      )
    );
  });

  itineraryInvalid$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.ItineraryInvalid),
        map(action => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Please fix before submitting:',
              message: action.payload.join('<br />')
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  submitRideSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ConfirmRideSubscriptionSubmission),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) => {
        const rideSubscription = new CreateRideSubscriptionRequest();

        rideSubscription.itinerary = this.itinerarySanitizer.sanitize(state.itinerary);
        rideSubscription.startDate = state.subscriptionStartDate;
        rideSubscription.endDate = state.subscriptionEndDate;
        rideSubscription.days = state.selectedDaysOfTheWeek.join();

        return this.rideSubscriptionService.saveRideSubscription(rideSubscription).pipe(
          map(rideSubscriptionDetail =>
            OrganizationRiderActions.SubmitRideSubscriptionSuccess({ rideSubscriptionDetail: rideSubscriptionDetail })
          ),
          catchError(error => of(OrganizationRiderActions.SubmitRideSubscriptionFail({ error })))
        );
      })
    );
  });

  modifyOrganizationRiderDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ModifyOrganizationRiderDetail),
      switchMap(action => {
        return this.organizationRiderService
          .patchOrganizationRider(action.payload.organizationRiderDetail, action.payload.organizationRiderPool)
          .pipe(
            mergeMap(organizationRiderDetail => {
              if (action.payload.organizationRiderServiceLevels && action.payload.organizationRiderServiceLevels.length > 0) {
                const serviceLevelOperations = action.payload.organizationRiderServiceLevels.map(orsl => {
                  orsl.organizationRiderId = organizationRiderDetail.organizationRiderId;
                  if (orsl.enabled) {
                    return this.organizationRiderService.addOrganizationRiderServiceLevel(
                      action.payload.organizationRiderDetail.organizationRiderId,
                      orsl
                    );
                  } else {
                    return this.organizationRiderService.disableOrganizationRiderServiceLeve(
                      action.payload.organizationRiderDetail.organizationRiderId,
                      orsl.serviceLevel.id
                    );
                  }
                });

                organizationRiderDetail.organizationRiderServiceLevels = action.payload.organizationRiderServiceLevels;

                return forkJoin(serviceLevelOperations).pipe(
                  map(() => OrganizationRiderActions.ModifyOrganizationRiderDetailSuccess({ organizationRiderDetail })),
                  catchError(error => {
                    error = this.checkDuplicateCustomerIdError(error);
                    return of(OrganizationRiderActions.ModifyOrganizationRiderDetailFail({ error }));
                  })
                );
              } else {
                return of(OrganizationRiderActions.ModifyOrganizationRiderDetailSuccess({ organizationRiderDetail }));
              }
            }),
            catchError(error => {
              error = this.checkDuplicateCustomerIdError(error);
              return of(OrganizationRiderActions.ModifyOrganizationRiderDetailFail({ error }));
            })
          );
      })
    );
  });

  submitRideSubscriptionSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.SubmitRideSubscriptionSuccess),
        map(() => this.router.navigate(['new-itinerary', 'ride-subscription-success']))
      );
    },
    { dispatch: false }
  );

  submitRideSubscriptionFail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.SubmitRideSubscriptionFail),
        withLatestFrom(this.store$.select(organizationRiderState)),
        map(([action, state]) => {
          let errorTitle;
          let links;

          if (RideErrorConstants.OVERLAPPING_RIDE_REQUEST.test(action.error.message)) {
            errorTitle = 'Ride Scheduling Conflict';
            action.error.message =
              'Subscription cannot be created because one or more ride requests in this subscription will conflict with existing reservations.';
          } else if (RideErrorConstants.RIDE_LIMIT_EXCEEDED.test(action.error.message)) {
            errorTitle = 'Ride Limit Error';
            action.error.message = `The rider does not have sufficient rides remaining in one or more ride limit periods. <br /> Review the rider's ride limits and adjust the subscription accordingly, then try again.`;
            links = [
              {
                text: 'Open rider profile &gt;',
                handler: () => {
                  this.router.navigate([`/riders/details/${state.rider.organizationRiderId}/organization/${state.rider.organizationId}`]);
                  this.dialog.closeAll();
                }
              }
            ];
          } else if (RideErrorConstants.PICKUP_DATE_OUTSIDE_OF_OPERATING_HOURS.test(action.error.message)) {
            errorTitle = 'Rides Outside Program Operating Hours';
            action.error.message = 'Subscription cannot be created because one or more rides are outside program operating hours.';
          } else if (RideErrorConstants.PICKUPDATE_AFTER_SUBSCRIPTION_ENDDATE.test(action.error.message)) {
            errorTitle = 'No Rides Created';
            action.error.message =
              'No rides will be created because the selected days do not occur within the subscription date range.<br /> Please check the subscription information to ensure it is correct.';
          } else if (RideErrorConstants.RIDE_REQUEST_OVERLAPS_RIDER_SUSPENSION.test(action.error.message)) {
            errorTitle = 'Rider Suspended';
            action.error.message = 'Ride requests cannot have a pickup time that overlaps with the rider’s suspension period.';
          } else {
            errorTitle = 'Error';
            action.error.message = 'Something went wrong and the subscription could not be created';
          }

          this.dialog.open(AlertDialogComponent, {
            data: {
              svgIcon: 'uz-alert-circle-outline',
              title: errorTitle,
              message: action.error.message,
              links: links
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  submitItinerary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ConfirmSubmission),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) => {
        // Sanitize and submit itinerary.
        return this.itineraryService.save(this.itinerarySanitizer.sanitize(state.itinerary)).pipe(
          map(savedItinerary => OrganizationRiderActions.SubmitItinerarySuccess({ itinerary: savedItinerary })),
          catchError(error => of(OrganizationRiderActions.SubmitItineraryFail({ error })))
        );
      })
    );
  });

  submitItinerarySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SubmitItinerarySuccess),
      map(() => TabActions.navigateAssumeCurrentTab({ route: '/new-itinerary/success' }))
    );
  });

  openEditUzurvRiderDetail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenEditUzurvRiderDetail),
        withLatestFrom(this.store$.select(organizationRiderState)),
        map(([_, state]) => {
          this.dialog.open(EditRiderDialogComponent, {
            data: {
              uzurvRider: state.uzurvRider,
              riderProfileDetail: state.riderProfileDetail,
              organizationRiderAddress: state.organizationRiderAddress
            },
            width: '55vw'
          });
        })
      );
    },
    { dispatch: false }
  );

  openAddOrganizationRiderDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenAddOrganizationRiderDialog),
        withLatestFrom(this.store$.select(organizationRiderState)),
        map(([_, state]) => {
          this.dialog.open(AddOrganizationRiderDialogComponent, {
            data: {
              uzurvRider: state.uzurvRider,
              organizations: state.organizations,
              organizationServiceLevels: state.organizationServiceLevels,
              organizationRiderServiceLevels: state.organizationRiderServiceLevels,
              organizationRiderDetails: state.organizationRiderDetails
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  openEditOrganizationRiderDetail$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenEditOrganizationRiderDetail),
        withLatestFrom(this.store$.select(organizationRiderState)),
        map(([action, state]) => {
          this.dialog.open(EditOrganizationRiderDialogComponent, {
            data: {
              organizationRiderDetail: action.payload.organizationRiderDetail,
              organizations: state.organizations,
              organization: state.organizations.find(org => org.id === action.payload.organizationRiderDetail.organizationId),
              organizationServiceLevels: action.payload.organizationServiceLevels,
              organizationRiderPools: action.payload.organizationRiderPools
            },
            width: '55vw'
          });
        })
      );
    },
    { dispatch: false }
  );

  updateOrganizationRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.UpdateOrganizationRider),
      switchMap(action =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: 'Save changes to this rider?',
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService.patchOrganizationRider(action.payload.organizationRider).pipe(
                  switchMap(organizationRider => {
                    !isNullOrUndefined(action.payload.dialogRef) ? action.payload.dialogRef.close() : null;
                    if (!isNullOrUndefined(action.payload.address)) {
                      return this.organizationRiderService.updateOrganizationRiderAddress(action.payload.address).pipe(
                        map(address => {
                          return OrganizationRiderActions.UpdateOrganizationRiderSuccess({
                            payload: {
                              organizationId: organizationRider.organizationId,
                              organizationRiderId: organizationRider.organizationRiderId,
                              address: address
                            }
                          });
                        }),
                        catchError(error => {
                          return of(OrganizationRiderActions.CreateOrganizationRiderFail({ error }));
                        })
                      );
                    } else {
                      return of(
                        OrganizationRiderActions.UpdateOrganizationRiderSuccess({
                          payload: {
                            organizationId: organizationRider.organizationId,
                            organizationRiderId: organizationRider.organizationRiderId
                          }
                        })
                      );
                    }
                  }),

                  catchError(error => {
                    error = this.checkDuplicateCustomerIdError(error);
                    return of(OrganizationRiderActions.UpdateOrganizationRiderFail({ error }));
                  })
                );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  updateUzurvRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.UpdateUzurvRider),
      switchMap(action => {
        return this.riderManagementService.updateRider(action.payload.rider.id, action.payload.rider).pipe(
          switchMap(rider => {
            if (!isNullOrUndefined(action.payload.address)) {
              return this.organizationRiderService
                .updateOrganizationRiderAddress(action.payload.address)
                .pipe(
                  map(address =>
                    OrganizationRiderActions.UpdateUzurvRiderSuccess({ payload: { rider, organizationRiderAddress: address } })
                  )
                );
            }
            return of(OrganizationRiderActions.UpdateUzurvRiderSuccess({ payload: { rider } }));
          }),
          catchError(error => of(OrganizationRiderActions.UpdateUzurvRiderFail({ error })))
        );
      })
    );
  });

  declineRiderEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.DeclineRiderEmail),
      switchMap(action =>
        this.organizationRiderService.patchOrganizationRider(action.organizationRiderDetail).pipe(
          map(organizationRider =>
            OrganizationRiderActions.UpdateOrganizationRiderSuccess({
              payload: {
                organizationId: organizationRider.organizationId,
                organizationRiderId: organizationRider.organizationRiderId
              }
            })
          ),
          catchError(error => of(OrganizationRiderActions.UpdateOrganizationRiderFail({ error })))
        )
      )
    );
  });

  removeFavoriteDriver$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.RemoveFavoriteDriver),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to remove ${action.driver.firstName} from ${state.rider.firstName} 's favorite drivers list?`,
              isDestructive: true
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService
                  .removeFavoriteDriverFromRiderWithId(state.rider.organizationRiderId, action.driver.id)
                  .pipe(
                    map(
                      () =>
                        OrganizationRiderActions.RemoveFavoriteDriverSuccess({
                          payload: {
                            organizationId: state.rider.organizationId,
                            organizationRiderId: state.rider.organizationRiderId
                          }
                        }),
                      catchError(error => of(OrganizationRiderActions.RemoveFavoriteDriverFail({ error })))
                    )
                  );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  removeIgnoredDriver$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.RemoveIgnoredDriver),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to remove ${action.driver.firstName} from ${state.rider.firstName} 's ignored drivers list?`,
              isDestructive: true
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService.removeIgnoredDriverFromRider(state.rider.organizationRiderId, action.driver.id).pipe(
                  map(
                    () =>
                      OrganizationRiderActions.RemoveIgnoredDriverSuccess({
                        payload: {
                          organizationId: state.rider.organizationId,
                          organizationRiderId: state.rider.organizationRiderId,
                          driver: action.driver
                        }
                      }),
                    catchError(error => of(OrganizationRiderActions.RemoveIgnoredDriverFail({ error })))
                  )
                );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  getPaymentMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      switchMap(action =>
        this.organizationService.listPaymentMethodsByOrganizationId(action.payload.organizationId).pipe(
          map(organizationPaymentMethods => OrganizationRiderActions.GetPaymentMethodsSuccess({ organizationPaymentMethods })),
          catchError(error => of(OrganizationRiderActions.GetPaymentMethodsFail({ error })))
        )
      )
    );
  });

  getCardTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess),
      switchMap(_ =>
        this.paymentAccountService.getCardTypes().pipe(
          map(cardTypes => OrganizationRiderActions.GetCardTypesSuccess({ cardTypes })),
          catchError(error => of(OrganizationRiderActions.GetCardTypesFail({ error })))
        )
      )
    );
  });

  createPaymentAccount = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.CreatePaymentAccount),
      withLatestFrom(this.store$.select(organizationRiderState), this.store$.select(getLatestChargeOrder)),
      switchMap(([action, state, latestChargeOrder]) => {
        action.payload.paymentAccount.personId = state.rider.riderId;
        action.payload.paymentAccount.chargeOrder = latestChargeOrder + 100;

        // Credit Card Validation
        try {
          PaymentUtil.validateCreditCard(
            action.payload.paymentAccount.cardNumber,
            action.payload.paymentAccount.cardExpiration,
            state.cardTypes
          );
        } catch (error) {
          return of(OrganizationRiderActions.CreatePaymentAccountFail({ error }));
        }

        // Card Expiration
        action.payload.paymentAccount.cardExpiration = PaymentUtil.formatCreditCardExpirationDate(
          action.payload.paymentAccount.cardExpiration
        );

        // Payment Method
        const cardPaymentMethod = new PaymentMethod();
        cardPaymentMethod.code = PaymentMethodConstants.CARD;

        action.payload.paymentAccount.paymentMethod = cardPaymentMethod;

        // Card Type
        const cardTypeCode: string = PaymentUtil.getCardTypeCodeFromCreditCard(action.payload.paymentAccount.cardNumber);
        action.payload.paymentAccount.cardType = state.cardTypes.find(ct => ct.code === cardTypeCode);

        // Save
        return this.riderManagementService.createPaymentAccount(state.rider.organizationRiderId, action.payload.paymentAccount).pipe(
          map(paymentAccount => {
            action.payload.dialogRef.close();
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Payment Account Created',
                message: `${paymentAccount.cardType.name} ending in: ${paymentAccount.cardNumber} was successfully created`
              }
            });
            return OrganizationRiderActions.CreatePaymentAccountSuccess({ paymentAccount });
          }),
          catchError(error => of(OrganizationRiderActions.CreatePaymentAccountFail({ error })))
        );
      })
    );
  });

  editPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.EditPaymentAccount),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) => {
        // Credit Card Validation
        try {
          PaymentUtil.validateCreditCardExpirationDate(action.payload.paymentAccount.cardExpiration);
        } catch (error) {
          return of(OrganizationRiderActions.EditPaymentAccountFail({ error }));
        }

        // Expiration Date
        action.payload.paymentAccount.cardExpiration = PaymentUtil.formatCreditCardExpirationDate(
          action.payload.paymentAccount.cardExpiration
        );

        return this.organizationRiderService.updatePaymentAccount(state.rider.organizationRiderId, action.payload.paymentAccount).pipe(
          map(paymentAccount => {
            action.payload.dialogRef.close();
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Payment Account Updated',
                message: `${paymentAccount.cardType.name} ending in: ${paymentAccount.cardNumber} was successfully updated`
              }
            });

            return OrganizationRiderActions.EditPaymentAccountSuccess({ paymentAccount });
          }),
          catchError(error => of(OrganizationRiderActions.EditPaymentAccountFail({ error })))
        );
      })
    );
  });

  deletePaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.DeletePaymentAccount),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Delete Payment Account',
              message: `Are you sure you want to delete this payment account?
          If you remove all payment accounts before entering a new payment account,
          all future trips are cancelled`,
              isDestructive: true
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService
                  .deletePaymentAccount(state.rider.organizationRiderId, action.payload.paymentAccount.id)
                  .pipe(
                    map(() => {
                      action.payload.dialogRef.close();
                      this.dialog.open(AlertDialogComponent, {
                        data: {
                          title: 'Success',
                          message: `Payment Account Successfully Deleted`
                        }
                      });
                      return OrganizationRiderActions.DeletePaymentAccountSuccess();
                    }),
                    catchError(error => of(OrganizationRiderActions.DeletePaymentAccountFail({ error })))
                  );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  getOutstandingCharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationSuccess, OrganizationRiderActions.EditPaymentAccountSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(
        ([_, state]) =>
          this.authDataService.hasPermission({ any: ['payment_processing'], all: [] }) &&
          state.organization &&
          state.organization.riderPaymentRequired &&
          state.organization.riderPaymentAccountEnabled
      ),
      switchMap(([_, state]) =>
        this.organizationRiderService.listAllCharges(state.rider.organizationRiderId, true).pipe(
          map(outstandingCharges => OrganizationRiderActions.GetOutstandingChargesSuccess({ outstandingCharges })),
          catchError(error => of(OrganizationRiderActions.GetOutstandingChargesFail({ error })))
        )
      )
    );
  });

  submitPaymentForOutstandingCharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SubmitPaymentForOutstandingCharges),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) => {
        const selectedOrderIds = [];
        let totalAmount = 0;
        action.payload.selectedCharges.forEach(charge => {
          selectedOrderIds.push(charge.id);
          totalAmount += charge.amount;
        });
        return this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to make a payment of ${this.currencyPipe.transform(totalAmount, 'USD', 'symbol')}`
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.organizationRiderService
                  .collectPaymentForCharges(state.rider.organizationRiderId, action.payload.selectedPaymentAccount.id, selectedOrderIds)
                  .pipe(
                    map(payment => {
                      this.dialog.open(AlertDialogComponent, {
                        data: {
                          title: `Payment status: ${payment.paymentStatus.name}`,
                          message: payment.paymentStatus.description
                        }
                      });

                      return OrganizationRiderActions.SubmitPaymentForOutstandingChargesSuccess({
                        payload: {
                          organizationId: state.rider.organizationId,
                          organizationRiderId: state.rider.organizationRiderId
                        }
                      });
                    }),
                    catchError(error => of(OrganizationRiderActions.SubmitPaymentForOutstandingChargesFail({ error })))
                  );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          );
      })
    );
  });

  getPayments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationSuccess, OrganizationRiderActions.PagePaymentsList),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(
        ([_, state]) =>
          this.authDataService.hasPermission({ any: ['payment_processing'], all: [] }) &&
          state.organization &&
          state.organization.riderPaymentRequired &&
          state.organization.riderPaymentAccountEnabled
      ),
      switchMap(([_, state]) =>
        this.organizationRiderService
          .listPaymentsByOrganizationRiderId(state.rider.organizationRiderId, state.paymentHistoryPageIndex, state.paymentHistoryPageSize)
          .pipe(
            map(payments => OrganizationRiderActions.GetPaymentsSuccess({ payments })),
            catchError(error => of(OrganizationRiderActions.GetPaymentsFail({ error })))
          )
      )
    );
  });

  openNewPaymentDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenNewPaymentDialog),
        withLatestFrom(this.store$.select(organizationRiderState)),
        filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
        map(([_, state]) =>
          this.dialog.open(NewPaymentDialogComponent, {
            data: {
              paymentAccounts: state.riderPaymentAccounts,
              organizationRiderId: state.rider.organizationRiderId
            }
          })
        )
      );
    },
    { dispatch: false }
  );

  createPayment = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.CreatePayment),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
      switchMap(([action, state]) =>
        this.organizationRiderService.createPayment(state.rider.organizationRiderId, action.payload.payment).pipe(
          map(payment => {
            action.payload.dialogRef.close();

            this.dialog.open(AlertDialogComponent, {
              data: {
                title: `Payment status: ${payment.paymentStatus.name}`,
                message: payment.paymentStatus.description
              }
            });

            return OrganizationRiderActions.CreatePaymentSuccess({
              payload: {
                organizationRiderId: state.rider.organizationRiderId,
                organizationId: state.rider.organizationId
              }
            });
          }),
          catchError(error => of(OrganizationRiderActions.CreatePaymentFail({ error })))
        )
      )
    );
  });

  voidPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.VoidPayment),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to void this payment with an amount of ${this.currencyPipe.transform(
                action.payment.amount,
                'USD',
                'symbol'
              )}`
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              action.payment.paymentStatus.name = PaymentStatusConstants.VOID_REQUESTED;
              if (confirmed) {
                return this.organizationRiderService.updateProcessedPayment(state.rider.organizationRiderId, action.payment).pipe(
                  map(voidedPayment => {
                    this.dialog.open(AlertDialogComponent, {
                      data: {
                        title: `Payment status: ${voidedPayment.paymentStatus.name}`,
                        message: voidedPayment.paymentStatus.description
                      }
                    });

                    return OrganizationRiderActions.VoidPaymentSuccess({
                      payload: {
                        organizationRiderId: state.rider.organizationRiderId,
                        organizationId: state.rider.organizationId
                      }
                    });
                  }),
                  catchError(error => of(OrganizationRiderActions.VoidPaymentFail({ error })))
                );
              }
            })
          )
      )
    );
  });

  resubmitPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ResubmitPayment),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
      switchMap(([action, state]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: `Are you sure you want to resubmit this payment with an amount of ${this.currencyPipe.transform(
                action.payment.amount,
                'USD',
                'symbol'
              )}`
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                const resubmitPayment: Payment = action.payment;
                resubmitPayment.paymentStatus.name = PaymentStatusConstants.AWAITING_PROCESSING;
                resubmitPayment.paymentStatus.id = 4;
                state.riderPaymentAccounts.forEach(paymentAccount => {
                  if (paymentAccount.id === resubmitPayment.paymentAccount.id) {
                    resubmitPayment.paymentAccount = paymentAccount;
                  }
                });
                return this.organizationRiderService.updateProcessedPayment(state.rider.organizationRiderId, resubmitPayment).pipe(
                  map(resubmittedPayment => {
                    this.dialog.open(AlertDialogComponent, {
                      data: {
                        title: `Payment status: ${resubmittedPayment.paymentStatus.name}`,
                        message: resubmittedPayment.paymentStatus.description
                      }
                    });
                    return OrganizationRiderActions.ResubmitPaymentSuccess({
                      payload: {
                        organizationRiderId: state.rider.organizationRiderId,
                        organizationId: state.rider.organizationId
                      }
                    });
                  }),
                  catchError(error => of(OrganizationRiderActions.ResubmitPaymentFail({ error })))
                );
              } else {
                return of(OrganizationRiderActions.AbandonSubmission());
              }
            })
          )
      )
    );
  });

  openRefundDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.OpenRefundDialog),
        filter(action => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
        map(action => {
          this.dialog
            .open(RefundPaymentDialogComponent, {
              data: {
                payment: action.payment
              }
            })
            .afterClosed();
        })
      );
    },
    { dispatch: false }
  );

  refundPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.RefundPayment),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
      switchMap(([action, state]) => {
        action.payload.payment.paymentStatus.name = PaymentStatusConstants.REFUND_REQUESTED;
        action.payload.payment.createdDate = new Date();
        return this.organizationRiderService.updateProcessedPayment(state.rider.organizationRiderId, action.payload.payment).pipe(
          map(refundedPayment => {
            action.payload.dialogRef.close();
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: `Payment status: ${refundedPayment.paymentStatus.name}`,
                message: refundedPayment.paymentStatus.description
              }
            });

            return OrganizationRiderActions.RefundPaymentSuccess({
              payload: {
                organizationRiderId: state.rider.organizationRiderId,
                organizationId: state.rider.organizationId
              }
            });
          }),
          catchError(error => of(OrganizationRiderActions.RefundPaymentFail({ error })))
        );
      })
    );
  });

  expandPayment = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ExpandPayment),
      switchMap(action => of(OrganizationRiderActions.GetExpandedPaymentTransactions()))
    );
  });

  getPaymentTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetExpandedPaymentTransactions),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => this.authDataService.hasPermission({ any: ['payment_processing'], all: [] })),
      switchMap(([_, state]) => {
        if (state.expandedPayment) {
          return this.organizationRiderService
            .listTransactionsByOrganizationRiderIdAndPaymentId(state.rider.organizationRiderId, state.expandedPayment.id)
            .pipe(
              map(transactions => OrganizationRiderActions.GetExpandedPaymentTransactionsSuccess({ transactions })),
              catchError(error => of(OrganizationRiderActions.GetExpandedPaymentTransactionsFail({ error })))
            );
        } else {
          return of(OrganizationRiderActions.AbandonSubmission());
        }
      })
    );
  });

  getNotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess, OrganizationRiderActions.AddNoteSuccess),
      withLatestFrom(this.store$.select(getRider)),
      filter(([action, rider]) => this.authDataService.hasPermission({ any: ['customer_service_note_read'], all: [] })),
      switchMap(([action, rider]) => {
        if (rider) {
          return this.customerServiceNoteService
            .getCustomerServiceNotesForObjectIdAndName(rider.organizationRiderId, ObjectConstants.ORGANIZATION_RIDER)
            .pipe(
              map(
                notes => OrganizationRiderActions.GetNotesSuccess({ payload: notes }),
                catchError(error => of(OrganizationRiderActions.GetNotesFail({ error })))
              )
            );
        } else {
          return of(OrganizationRiderActions.GetNotesFail({ error: new ApplicationException('Could not get notes') }));
        }
      })
    );
  });

  addNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.AddNote),
      withLatestFrom(this.store$.select(getRider)),
      switchMap(([action, rider]) => {
        action.payload.objectId = rider.organizationRiderId;
        action.payload.objectName = ObjectConstants.ORGANIZATION_RIDER;
        return this.customerServiceNoteService.createCustomerServiceNote(action.payload).pipe(
          map(note => OrganizationRiderActions.AddNoteSuccess({ payload: note })),
          catchError(error => of(OrganizationRiderActions.AddNoteFail({ error })))
        );
      })
    );
  });

  confirmSendUzurvRideAppInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.ConfirmSendUzurvRideAppInvitation),
      switchMap(action =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are You Sure?',
              message: 'Are you sure you want to send this invitation?',
              isDestructive: false
            }
          })
          .afterClosed()
          .pipe(
            map(confirmed => {
              if (confirmed) {
                return OrganizationRiderActions.SendUzurvRideAppInvitation({
                  organizationRiderDetail: action.organizationRiderDetail,
                  fromNewItinerary: action.fromNewItinerary
                });
              }

              return OrganizationRiderActions.AbandonSendUzurvRideAppInvitation();
            })
          )
      )
    );
  });

  sendUzurvRideAppInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SendUzurvRideAppInvitation),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) =>
        this.organizationRiderService
          .sendUzurvRideAppInvitationToOrganizationRider(action.organizationRiderDetail.organizationRiderId)
          .pipe(
            map(success =>
              OrganizationRiderActions.SendUzurvRideAppInvitationSuccess({
                rider: action.fromNewItinerary ? action.organizationRiderDetail : state.uzurvRider
              })
            ),
            catchError(error => of(OrganizationRiderActions.SendUzurvRideAppInvitationFail({ error })))
          )
      )
    );
  });

  sendUzurvRideAppInvitationSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.SendUzurvRideAppInvitationSuccess),
        map(action => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Invitation Sent',
              message: `UZURV Ride invitation sent to ${action.rider.firstName}`
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  handleFavoriteLocationError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrganizationRiderActions.AddFavoriteLocationFail),
        withLatestFrom(this.store$.select(organizationRiderState)),
        map(([action, state]) => {
          if (LocationErrorConstants.FAVORITE_LOCATION_ALREADY_EXISTS.test(action.payload.error.message)) {
            const existingFavoriteLocation: FavoriteLocation = state.riderFavoriteLocations.find(
              favoriteLocation => favoriteLocation.location.id === action.payload.location.id
            );

            this.dialog
              .open(ConfirmationDialogComponent, {
                data: {
                  title: 'Rename Favorite Location?',
                  message: `${existingFavoriteLocation.location.streetAddress1} is already saved as "${existingFavoriteLocation.label}". Would you like to rename it?`,
                  isDestructive: false
                }
              })
              .afterClosed()
              .subscribe(confirmed => {
                if (confirmed) {
                  const favoriteLocation: FavoriteLocation = new FavoriteLocation();
                  favoriteLocation.riderId = state.rider.riderId;
                  favoriteLocation.location = action.payload.location;
                  favoriteLocation.label = action.payload.label;
                  this.store$.dispatch(
                    OrganizationRiderActions.ReplaceFavoriteLocation({
                      payload: { toDelete: existingFavoriteLocation, toCreate: favoriteLocation }
                    })
                  );
                }
              });
          } else if (LocationErrorConstants.FAVORITE_LOCATION_LABEL_ALREADY_EXISTS.test(action.payload.error.message)) {
            // eslint-disable-next-line no-redeclare
            const existingFavoriteLocation: FavoriteLocation = state.riderFavoriteLocations.find(
              favoriteLocation => favoriteLocation.label === action.payload.label
            );

            this.dialog
              .open(ConfirmationDialogComponent, {
                data: {
                  title: 'Replace Favorite Location?',
                  message: `"${existingFavoriteLocation.label}" is already saved as ${existingFavoriteLocation.location.streetAddress1}. Would you like to replace it?`,
                  isDestructive: false
                }
              })
              .afterClosed()
              .subscribe(confirmed => {
                if (confirmed) {
                  const favoriteLocation: FavoriteLocation = new FavoriteLocation();
                  favoriteLocation.riderId = state.rider.riderId;
                  favoriteLocation.location = action.payload.location;
                  favoriteLocation.label = action.payload.label;
                  this.store$.dispatch(
                    OrganizationRiderActions.ReplaceFavoriteLocation({
                      payload: { toDelete: existingFavoriteLocation, toCreate: favoriteLocation }
                    })
                  );
                }
              });
          } else {
            this.dialog.open(AlertDialogComponent, {
              data: {
                title: 'Error',
                message: action.payload.error.message
              }
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  fetchAvailableProviders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.FetchAvailableProviders),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([action, state]) => {
        let serviceLevelCode: string = ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE;

        if (action.serviceLevelCode === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV) {
          serviceLevelCode = ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE;
        }

        return this.transitProgramService
          .listActiveTransitProgramProvidersForTransitProgramAndServiceLevelCode(action.transitProgramId, serviceLevelCode)
          .pipe(
            map(transitProgramProviders => {
              let availableProviders = [];

              if (!isNullOrUndefined(transitProgramProviders)) {
                availableProviders = transitProgramProviders.map(transitProgramProvider => transitProgramProvider.provider);
              }

              availableProviders.sort((a, b) => a.name.localeCompare(b.name));

              return OrganizationRiderActions.FetchAvailableProvidersSuccess({
                availableProviders: availableProviders
              });
            }),
            catchError(err => of(OrganizationRiderActions.FetchAvailableProvidersFail(err)))
          );
      })
    );
  });

  updateShouldShowPreferredProviderSelection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.SelectDate,
        OrganizationRiderActions.NewLeg,
        OrganizationRiderActions.RemoveLeg,
        OrganizationRiderActions.UpdateLeg
      ),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.newItineraryValidationService.validate(state, true).pipe(
          map(errors =>
            OrganizationRiderActions.ShouldShowPreferredProviderSelection({
              shouldShowPreferredProviderSelection: errors.length === 0 && state.availableProviders.length > 1
            })
          )
        )
      )
    );
  });

  updateShouldShowPaymentSelection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.SelectDate,
        OrganizationRiderActions.SelectSubscriptionStartDate,
        OrganizationRiderActions.SelectSubscriptionEndDate,
        OrganizationRiderActions.NewLeg,
        OrganizationRiderActions.RemoveLeg,
        OrganizationRiderActions.UpdateLeg,
        OrganizationRiderActions.SelectPreferredProvider
      ),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) => {
        if (state.rider.organizationChargeInTime && !state.organization.riderPaymentRequired) {
          return of(OrganizationRiderActions.ShouldShowPaymentSelection({ shouldShowPaymentSelection: true }));
        }

        if (!state.organization.riderPaymentRequired) {
          return of(OrganizationRiderActions.ShouldShowPaymentSelection({ shouldShowPaymentSelection: false }));
        }

        // TODO (NewItin Refactor): Check out this logic for determining whether to show payment selection, look into a Organization Config to drive this logic.
        if (state.availableProviders?.length > 1 && isNullOrUndefined(state.preferredProviderId)) {
          return of(OrganizationRiderActions.ShouldShowPaymentSelection({ shouldShowPaymentSelection: false }));
        }

        return this.newItineraryValidationService
          .validate(state, true)
          .pipe(map(errors => OrganizationRiderActions.ShouldShowPaymentSelection({ shouldShowPaymentSelection: errors.length === 0 })));
      })
    );
  });

  updateShouldShowMobilityProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetRiderProfileSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) => {
        const hasPermission = this.authDataService.hasPermission({
          any: [PermissionsConstants.MOBILITY_PROFILE_READ, PermissionsConstants.MOBILITY_PROFILE_WRITE],
          all: []
        });
        const hasEditPermission = this.authDataService.hasPermission({ any: [PermissionsConstants.MOBILITY_PROFILE_WRITE], all: [] });
        return of(
          OrganizationRiderActions.ShouldShowMobilityProfile({
            shouldShowMobilityProfile: !isNullOrUndefined(state.riderMobilityProfile) && hasPermission,
            shouldDisableMobilityProfileSelection: !isNullOrUndefined(state.riderMobilityProfile) && !hasEditPermission
          })
        );
      })
    );
  });

  selectRiderDetailTab$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.SelectRiderDetailTab),
      withLatestFrom(this.store$.select(organizationRiderState)),

      switchMap(() => of(OrganizationRiderActions.GetRiderTrips()))
    );
  });

  getTrips$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrganizationRiderActions.GetRiderTrips,
        OrganizationRiderActions.TripsPagination,
        OrganizationRiderActions.GetOrganizationRiderDetailSuccess
      ),
      withLatestFrom(this.store$.select(organizationRiderState)),
      switchMap(([_, state]) =>
        this.riderManagementService.listTripsByRiderId(state.rider.riderId, state.tripsPageIndex, state.tripsPageSize).pipe(
          map((trips: Array<Trip>) => GetRiderTripsSuccess({ trips: trips })),
          catchError(error => of(GetRiderTripsFail({ error })))
        )
      )
    );
  });

  getRiderSuspensions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.GetOrganizationRiderDetailSuccess, OrganizationRiderActions.RemoveRiderSuspensionSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([action, state]) =>
        this.authDataService.hasPermission({
          any: [PermissionsConstants.RIDER_SUSPENSION_READ],
          all: []
        })
      ),
      switchMap(([_, state]) =>
        this.riderManagementService.listRiderSuspensions(state.rider.riderId, true).pipe(
          map((riderSuspensions: Array<PersonSuspensionDetail>) =>
            OrganizationRiderActions.GetRiderSuspensionsSuccess({ riderSuspensions: riderSuspensions })
          ),
          catchError(error => of(OrganizationRiderActions.GetRiderSuspensionsFail({ error: error })))
        )
      )
    );
  });

  createRiderSuspension$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.CreateRiderSuspension),
      switchMap(action => {
        if (
          !this.authDataService.hasPermission({
            any: [PermissionsConstants.RIDER_SUSPENSION_CREATE, PermissionsConstants.RIDER_SUSPENSION_PERMANENT_CREATE],
            all: []
          })
        ) {
          return this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                title: 'Permission Denied',
                message: `You do not have permission to create a rider suspension.`,
                confirmText: 'OK',
                isDestructive: false
              }
            })
            .afterClosed()
            .pipe(
              map(() => {
                return OrganizationRiderActions.CreateRiderSuspensionCancelled();
              })
            );
        }

        return this.suspensionReasonService.listRiderSuspensionReasons().pipe(
          switchMap(suspensionReasons => {
            return this.dialog
              .open(PersonSuspensionDialog, {
                data: {
                  personType: PersonSuspensionTypeEnum.RIDER,
                  suspensionReasons: suspensionReasons.filter(reason => reason.active)
                },
                width: '498px'
              })
              .afterClosed()
              .pipe(
                switchMap((createSuspensionData: PersonSuspensionDialogResults) => {
                  if (isNullOrUndefined(createSuspensionData)) {
                    return of(OrganizationRiderActions.CreateRiderSuspensionCancelled());
                  }

                  let confirmationMessage: string;

                  if (createSuspensionData.suspensionReason.permanent) {
                    confirmationMessage = `Are you sure you want to suspend ${action.rider.firstName}? This will prevent them from scheduling new rides and will cancel all existing rides beginning tomorrow.`;
                  } else {
                    const effectiveDate = new Date(createSuspensionData.suspensionEffectiveDate);
                    const isEffectiveDateToday = DateUtil.isDateToday(moment(effectiveDate));

                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);

                    confirmationMessage = `Are you sure you want to suspend ${
                      action.rider.firstName
                    }? This will prevent them from scheduling new rides and will cancel all existing rides starting ${
                      isEffectiveDateToday
                        ? TimeZoneUtil.formatLocalTimeZoneDate(tomorrow, DateFormatConstants.M_D_YY_SLASH)
                        : TimeZoneUtil.formatLocalTimeZoneDate(effectiveDate, DateFormatConstants.M_D_YY_SLASH)
                    }.`;
                  }

                  return this.dialog
                    .open(ConfirmationDialogComponent, {
                      data: {
                        title: 'Confirm Suspension',
                        message: confirmationMessage,
                        confirmText: 'Confirm Suspension',
                        dismissText: 'Cancel',
                        isDestructive: true
                      }
                    })
                    .afterClosed()
                    .pipe(
                      switchMap(confirmed => {
                        if (isNullOrUndefined(confirmed)) {
                          return of(OrganizationRiderActions.CreateRiderSuspensionCancelled());
                        }

                        if (confirmed) {
                          const personSuspension = new PersonSuspension();
                          personSuspension.personId = action.rider.id;
                          personSuspension.effectiveAt = createSuspensionData.suspensionEffectiveDate;
                          personSuspension.durationHours = createSuspensionData.suspensionDuration;
                          personSuspension.suspensionReason = createSuspensionData.suspensionReason;
                          personSuspension.note = createSuspensionData.suspensionDetailNotes;

                          return this.riderManagementService.createRiderSuspension(action.rider.id, personSuspension).pipe(
                            map(riderSuspension => {
                              return OrganizationRiderActions.CreateRiderSuspensionSuccess({
                                riderSuspension: riderSuspension
                              });
                            }),
                            catchError(err => {
                              return of(OrganizationRiderActions.CreateRiderSuspensionFail({ error: err }));
                            })
                          );
                        }
                      })
                    );
                })
              );
          }),
          catchError(err => {
            return of(OrganizationRiderActions.CreateRiderSuspensionFail({ error: err }));
          })
        );
      })
    );
  });

  removeRiderSuspension$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.RemoveRiderSuspension),
      switchMap(action => {
        if (
          !this.authDataService.hasPermission({
            any: [PermissionsConstants.RIDER_SUSPENSION_DELETE, PermissionsConstants.RIDER_SUSPENSION_PERMANENT_DELETE],
            all: []
          })
        ) {
          return this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                title: 'Permission Denied',
                message: `You do not have permission to remove a rider suspension.`,
                confirmText: 'OK',
                isDestructive: false
              }
            })
            .afterClosed()
            .pipe(
              map(() => {
                return OrganizationRiderActions.RemoveRiderSuspensionCancelled();
              })
            );
        }

        return this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Remove Suspension',
              message: `Are you sure you want to remove this suspension?`,
              confirmText: 'Remove Suspension',
              dismissText: 'Cancel',
              isDestructive: true
            }
          })
          .afterClosed()
          .pipe(
            switchMap(confirmed => {
              if (confirmed) {
                return this.riderManagementService.removeRiderSuspension(action.riderId, action.riderSuspensionId).pipe(
                  map(() => {
                    return OrganizationRiderActions.RemoveRiderSuspensionSuccess({
                      riderSuspensionId: action.riderSuspensionId
                    });
                  }),
                  catchError(err => of(OrganizationRiderActions.RemoveRiderSuspensionFail({ error: err })))
                );
              }

              return of(OrganizationRiderActions.RemoveRiderSuspensionCancelled());
            })
          );
      })
    );
  });

  refreshRiderProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrganizationRiderActions.CreateRiderSuspensionSuccess, OrganizationRiderActions.RemoveRiderSuspensionSuccess),
      withLatestFrom(this.store$.select(organizationRiderState)),
      filter(([_, state]) => !isNullOrUndefined(state.rider)),
      switchMap(([_, state]) =>
        this.organizationRiderService.getRiderProfileByOrganizationRiderId(state.rider.organizationRiderId).pipe(
          map(riderProfile => {
            return OrganizationRiderActions.GetRiderProfileSuccess({ riderProfile });
          }),
          catchError(error => of(OrganizationRiderActions.GetRiderProfileFail({ error })))
        )
      )
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          OrganizationRiderActions.RiderSearchFail,
          OrganizationRiderActions.GetVehicleTypesFail,
          OrganizationRiderActions.GetAmenitiesFail,
          OrganizationRiderActions.GetRideRequestTypesFail,
          OrganizationRiderActions.GetOrganizationServiceLevelsFail,
          OrganizationRiderActions.GetOrganizationRiderServiceLevelsFail,
          OrganizationRiderActions.GetRiderPaymentAccountsFail,
          OrganizationRiderActions.SubmitItineraryFail,
          OrganizationRiderActions.GetOrganizationFail,
          OrganizationRiderActions.CreateOrganizationRiderFail,
          OrganizationRiderActions.CreateRiderWithProfilesAndServiceLevelsFail,
          OrganizationRiderActions.UpdateOrganizationRiderFail,
          OrganizationRiderActions.DisableOrganizationRiderServiceLevelFail,
          OrganizationRiderActions.EnableOrganizationRiderServiceLevelFail,
          OrganizationRiderActions.GetOrganizationsFail,
          OrganizationRiderActions.GetPaymentMethodsFail,
          OrganizationRiderActions.GetCardTypesFail,
          OrganizationRiderActions.EditPaymentAccountFail,
          OrganizationRiderActions.DeletePaymentAccountFail,
          OrganizationRiderActions.GetOutstandingChargesFail,
          OrganizationRiderActions.GetPaymentsFail,
          OrganizationRiderActions.CreatePaymentAccountFail,
          OrganizationRiderActions.SendUzurvRideAppInvitationFail,
          OrganizationRiderActions.ModifyOrganizationRiderDetailFail,
          OrganizationRiderActions.UpdateOrganizationRiderEligibilityFail,
          OrganizationRiderActions.UpdateUzurvRiderFail,
          OrganizationRiderActions.GetRiderProfileFail,
          OrganizationRiderActions.UpdateRiderMobilityProfileFail,
          OrganizationRiderActions.VerifyRiderMobilityProfileFail,
          OrganizationRiderActions.CreateRiderSuspensionFail,
          OrganizationRiderActions.GetRiderSuspensionsFail,
          OrganizationRiderActions.RemoveRiderSuspensionFail
        ),
        map(action => {
          let title = 'Error';

          if (action.error.message.includes('Ride requests are currently not being accepted for transit program')) {
            action.error.message = 'Ride requests are currently not being accepted for this transit program.';
          }
          if (action.error.message.includes('100 characters')) {
            action.error.message = 'The trip notes field must be less than 100 characters for transit programs with Lyft fall back enabled';
          }
          if (action.error.message.includes('must be different')) {
            action.error.message = 'The pick-up and drop-off points must be different';
          }
          if (action.error.message.includes('limit exceeded')) {
            action.error.message = 'The trip cannot be created because one of the ride limits has been reached.';
          }
          if (action.error.message.includes('E00042')) {
            action.error.message =
              'Payment processor only supports 10 payment accounts. If you believe this to be an error, contact your administrator';
          }
          if (action.error.message.includes('Street address 1 must not be null')) {
            action.error.message = 'Street address one must not be empty.';
          }
          if (action.error.message.includes('City must not be null')) {
            action.error.message = 'City must not be empty.';
          }
          if (action.error.message.includes('State code must not be null')) {
            action.error.message = 'State must not be empty.';
          }
          if (action.error.message.includes('Zip code must not be null')) {
            action.error.message = 'Zip code must not be empty.';
          }
          if (action.error.message.includes('There is one or more missing or invalid required fields')) {
            title = 'Verification Failed';
            action.error.message = 'One or more fields are missing or invalid';
          }
          if (RideErrorConstants.RIDE_REQUEST_OVERLAPS_RIDER_SUSPENSION.test(action.error.message)) {
            title = 'Rider Suspended';
            action.error.message = 'Ride requests cannot have a pickup time that overlaps with the rider’s suspension period.';
          }

          // Invalid and duplicate transaction

          if (PaymentAccountErrorConstants.TRANSACTION_DECLINED.test(action.error.message)) {
            title = 'Verification Failed';
            action.error.message =
              'The CVV code or billing address you entered for this card is incorrect. Please check your information and wait at least 2 minutes before trying again.';
          }
          if (PaymentAccountErrorConstants.DUPLICATE_TRANSACTION.test(action.error.message)) {
            title = 'Please Wait';
            action.error.message = 'Please wait at least 2 minutes before trying to resubmit your information.';
          }
          if (PaymentAccountErrorConstants.PAYMENT_ACCOUNT_ALREADY_ON_FILE.test(action.error.message)) {
            action.error.message = 'Payment Account Already On File.';
          }

          this.dialog.open(AlertDialogComponent, {
            data: {
              title: title,
              message: action.error.message
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  loading$ = createEffect(
    () => {
      return this.actions$.pipe(ofType(OrganizationRiderActions.ConfirmSubmission));
    },
    { dispatch: false }
  );

  doneLoading$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          OrganizationRiderActions.SubmitItinerarySuccess,
          OrganizationRiderActions.SubmitItineraryFail,
          OrganizationRiderActions.SubmitRideSubscriptionFail
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store$: Store<State<OrganizationRiderState>>,
    private riderManagementService: RiderManagementService,
    private rideRequestService: RideRequestService,
    private vehicleService: VehicleService,
    private amenityService: AmenityService,
    private organizationRiderService: OrganizationRiderService,
    private newItineraryValidationService: NewItineraryValidationService,
    private itineraryService: ItineraryService,
    private organizationService: OrganizationService,
    private dialog: MatDialog,
    private authDataService: AuthDataService,
    private transitProgramService: TransitProgramService,
    private paymentAccountService: PaymentAccountService,
    private customerServiceNoteService: CustomerServiceNoteService,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private itinerarySanitizer: ItinerarySanitizer,
    private rideSubscriptionService: RideSubscriptionService,
    private snackBar: MatSnackBar,
    private suspensionReasonService: SuspensionReasonService
  ) {}

  /**
   * Extracts an organization code (between brackets) from the error message, and returns a pretty error.
   * @param error
   * @private
   */
  private checkDuplicateCustomerIdError(error: any): Error {
    if (error.message.includes('An organizationRider with partnerIdentifier') && error.message.includes('already exists')) {
      const matches = error.message.match(/\[(.*?)\]/g);
      const strArr: Array<string> = matches.map(match => match.slice(1, -1));
      if (strArr.length === 2) {
        error.message = `The Customer ID you entered already exists in ${strArr.at(1)} and cannot be duplicated.`;
      }
    }
    return error;
  }
}
